/**
 * Google Vision AI Service
 * 
 * Note: For production, this should call a backend API endpoint that has the Google Vision API key.
 * Never expose your Google Cloud API key in the frontend.
 * 
 * This service analyzes food images and identifies dish types.
 */

// Mock implementation - Replace with actual API call to your backend
export const analyzeFoodImage = async (imageFile) => {
  try {
    // In production, this would call your backend API:
    // const response = await fetch('/api/vision/analyze', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ image: base64Image })
    // });
    // return await response.json();

    // For now, simulate AI analysis with a delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Mock AI response - In production, this comes from Google Vision API
    const mockLabels = [
      { description: 'Food', score: 0.98 },
      { description: 'Pizza', score: 0.85 },
      { description: 'Italian food', score: 0.75 },
      { description: 'Dish', score: 0.70 }
    ];

    // Determine dish type from labels
    const dishType = detectDishType(mockLabels);
    const cuisine = detectCuisine(mockLabels);

    return {
      success: true,
      labels: mockLabels,
      detectedDishType: dishType,
      detectedCuisine: cuisine,
      confidence: mockLabels[0]?.score || 0.8
    };
  } catch (error) {
    console.error('Error analyzing image:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Detect dish type from Vision API labels
 */
const detectDishType = (labels) => {
  const dishTypeKeywords = {
    'Pizza': ['pizza', 'pie'],
    'Pasta': ['pasta', 'spaghetti', 'noodles'],
    'Burger': ['burger', 'hamburger', 'sandwich'],
    'Sushi': ['sushi', 'sashimi', 'japanese'],
    'Taco': ['taco', 'mexican'],
    'Ramen': ['ramen', 'noodle soup'],
    'Curry': ['curry', 'indian'],
    'Steak': ['steak', 'beef', 'meat'],
    'Seafood': ['seafood', 'fish', 'shrimp'],
    'Dessert': ['dessert', 'cake', 'sweet'],
    'Salad': ['salad', 'vegetables']
  };

  const labelText = labels.map(l => l.description.toLowerCase()).join(' ');
  
  for (const [type, keywords] of Object.entries(dishTypeKeywords)) {
    if (keywords.some(keyword => labelText.includes(keyword))) {
      return type;
    }
  }

  return null;
};

/**
 * Detect cuisine from Vision API labels
 */
const detectCuisine = (labels) => {
  const cuisineKeywords = {
    'Italian': ['italian', 'pizza', 'pasta', 'spaghetti'],
    'Mexican': ['mexican', 'taco', 'burrito'],
    'Japanese': ['japanese', 'sushi', 'ramen'],
    'Chinese': ['chinese', 'noodles', 'dim sum'],
    'Indian': ['indian', 'curry', 'tandoori'],
    'Thai': ['thai', 'pad thai'],
    'French': ['french', 'cuisine'],
    'American': ['american', 'burger', 'fries'],
    'Mediterranean': ['mediterranean', 'greek'],
    'Korean': ['korean', 'kimchi']
  };

  const labelText = labels.map(l => l.description.toLowerCase()).join(' ');
  
  for (const [cuisine, keywords] of Object.entries(cuisineKeywords)) {
    if (keywords.some(keyword => labelText.includes(keyword))) {
      return cuisine;
    }
  }

  return null;
};

/**
 * Convert image file to base64 for API
 */
export const imageToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

